"use client";

import { useEffect, useState } from "react";
import {
  isConsentBannerEnabled,
  getAnalyticsConsent,
  acceptAnalyticsConsent,
  declineAnalyticsConsent,
} from "@/lib/posthog";

/**
 * Analytics consent gate (dark by default — NEXT_PUBLIC_CONSENT_BANNER === 'true').
 *
 * A small, quiet bottom-left card shown once, until the user chooses. Accept
 * initializes PostHog and drains any queued events; Decline opts out. The choice
 * persists in localStorage, so the card never nags a returning user. When the
 * flag is off it renders nothing and PostHog behaves exactly as before.
 */
export function ConsentBanner() {
  // Render nothing on the server / first paint, then decide client-side once
  // localStorage is readable — avoids a hydration mismatch.
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isConsentBannerEnabled() && getAnalyticsConsent() === null) {
      setVisible(true);
    }
  }, []);

  if (!visible) return null;

  const accept = () => {
    acceptAnalyticsConsent();
    setVisible(false);
  };
  const decline = () => {
    declineAnalyticsConsent();
    setVisible(false);
  };

  return (
    <div
      role="region"
      aria-label="Analytics consent"
      className="fixed bottom-6 left-6 z-40 w-[min(22rem,calc(100vw-3rem))] rounded-16 border border-border-muted bg-bg-base p-4 shadow-lg motion-safe:animate-slide-in"
    >
      <p className="text-3.5 leading-relaxed text-fg-muted">
        Vestra uses privacy-first analytics to improve your experience, and you
        can opt out anytime.
      </p>
      <div className="mt-3 flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={decline}
          className="rounded-8 px-3 py-1.5 text-3.5 text-fg-muted transition-colors hover:text-fg-default"
        >
          Decline
        </button>
        <button
          type="button"
          onClick={accept}
          className="rounded-8 bg-fg-default px-3 py-1.5 text-3.5 text-bg-base transition-opacity hover:opacity-90"
        >
          Accept
        </button>
      </div>
    </div>
  );
}
